<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import type { ItemDTO } from "@barkup/shared";
import { fetchItemObjectUrl, fetchItemThumbUrl } from "../api.js";
import { fileIconFor } from "../fileIcon.js";
import { senderLabel } from "../format.js";

const props = defineProps<{
  item: ItemDTO;
  trash: boolean;
  hasPrev: boolean;
  hasNext: boolean;
  /** "3 / 42" once the full count is known, otherwise just "3" while more pages can still load. */
  position: string;
}>();

const emit = defineEmits<{
  close: [];
  prev: [];
  next: [];
  delete: [id: string];
  restore: [id: string];
  download: [item: ItemDTO];
  /** The user wants to report this item — the parent routes to the /report
   * screen. Nothing has been filed yet at this point. */
  report: [];
}>();

const root = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);
let previouslyFocused: HTMLElement | null = null;

onMounted(() => {
  document.getElementById("app")?.setAttribute("inert", "");
  previouslyFocused = document.activeElement as HTMLElement | null;
  closeButton.value?.focus();
  window.addEventListener("keydown", onKeydown);
});
onUnmounted(() => {
  document.getElementById("app")?.removeAttribute("inert");
  window.removeEventListener("keydown", onKeydown);
  previouslyFocused?.focus();
});

function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") emit("close");
  else if (e.key === "ArrowLeft" && props.hasPrev) emit("prev");
  else if (e.key === "ArrowRight" && props.hasNext) emit("next");
}

const prefersReducedMotion =
  typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

const kindMeta = computed(
  () =>
    ({
      FILE: { icon: fileIconFor(props.item), tile: "bg-primary-container text-primary" },
      IMAGE: { icon: "image", tile: "bg-secondary-container text-secondary" },
      VIDEO: { icon: "movie", tile: "bg-surface-container-high text-on-surface-variant" },
      LINK: { icon: "link", tile: "bg-tertiary-container text-tertiary" },
    })[props.item.kind]
);

const title = computed(() => props.item.filename ?? props.item.ogTitle ?? props.item.linkUrl ?? "(ไม่มีชื่อ)");
const statusReady = computed(() => props.item.status === "READY" || props.item.status === "PARTIAL_SCAN");
// Trust item.kind first — mimeType can be null (LINE reported no Content-Type
// at ingest, see dto.ts / the backfillMime script), and keying the preview
// solely on it left IMAGE/VIDEO items whose type wasn't captured showing just
// an icon instead of the media — even though the grid tile (ItemTile keys off
// kind) rendered their thumbnail fine. mimeType still catches an image/video
// that was sent AS a file (kind=FILE but an image/* type).
const isImage = computed(() => props.item.kind === "IMAGE" || (props.item.mimeType ?? "").startsWith("image/"));
const isVideo = computed(() => props.item.kind === "VIDEO" || (props.item.mimeType ?? "").startsWith("video/"));

const sizeLabel = computed(() => {
  const bytes = Number(props.item.sizeBytes);
  if (!bytes) return null;
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
});

const dateTimeLabel = computed(() =>
  new Date(props.item.createdAt).toLocaleString("th-TH", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
);

// Media loading, keyed to the current item — resets whenever the active item changes
// (swipe, or a direct navigate) rather than persisting stale state across items.
const mediaUrl = ref<string | null>(null);
const mediaFailed = ref(false);
/**
 * The concrete reason a preview failed (HTTP status/message from the fetch, or
 * a render failure). Surfaced in the UI so a failure on a real device — where
 * there's no devtools — is diagnosable instead of a blank "couldn't preview".
 */
const mediaErrorMsg = ref<string | null>(null);
const mediaLoading = computed(() => (isImage.value || isVideo.value) && !mediaUrl.value && !mediaFailed.value);

/** The <img>/<video> element itself failing to render (e.g. a webview blocking a blob: URL) — distinct from the fetch failing. */
function onMediaElementError() {
  if (mediaUrl.value) mediaErrorMsg.value = "แสดงภาพไม่ได้ (เบราว์เซอร์บล็อกการแสดงผล)";
  mediaFailed.value = true;
}
// Showing the server thumbnail rather than the original. Browsing (open,
// swipe) costs tens of KB per image instead of the full file — the original
// is only fetched on an explicit zoom (loadFullImage). Items without a thumb
// (pre-thumbnail ingests, generation failures, videos) load the full object
// up front exactly as before.
const usingThumb = ref(false);

// Zoom state — used by the zoom handlers further down and reset by
// resetAndLoadMedia (below) on every item change.
const zoomed = ref(false);
const panX = ref(0);
const panY = ref(0);

/**
 * Reset per-item state and (re)load the media for the active item. Invoked from
 * onMounted (initial open) and a watch (swipe / direct navigate) — deliberately
 * NOT a `{ immediate: true }` watch. An immediate watch runs its body during
 * setup, before the `const`s it touches (the zoom state, declared below)
 * are initialized, which threw "Cannot access 'x' before initialization" and
 * aborted the load so the preview never appeared. A plain function called after
 * setup finishes sidesteps that whole class of temporal-dead-zone bug.
 */
function resetAndLoadMedia() {
  const itemId = props.item.id;
  mediaUrl.value = null;
  mediaFailed.value = false;
  mediaErrorMsg.value = null;
  usingThumb.value = false;
  zoomed.value = false;
  panX.value = 0;
  panY.value = 0;
  if (!isImage.value && !isVideo.value) return;

  // Guard every async assignment with the id it was started for — a fast swipe
  // past several images must not let a slow older fetch stomp the media of the
  // item now on screen.
  const loadFull = () =>
    fetchItemObjectUrl(itemId)
      .then((url) => {
        if (props.item.id !== itemId) return;
        usingThumb.value = false;
        mediaUrl.value = url;
      })
      .catch((err) => {
        if (props.item.id !== itemId) return;
        mediaErrorMsg.value = err instanceof Error ? err.message : String(err);
        mediaFailed.value = true;
      });

  if (isImage.value && props.item.hasThumb) {
    fetchItemThumbUrl(itemId)
      .then((url) => {
        if (props.item.id !== itemId) return;
        usingThumb.value = true;
        mediaUrl.value = url;
      })
      .catch(() => void loadFull()); // thumb missing/failed — degrade to the full-fetch path
    return;
  }
  void loadFull();
}

watch(() => props.item.id, resetAndLoadMedia);
onMounted(resetAndLoadMedia);

/** Swaps the thumbnail for the original — called when the user zooms in. */
function loadFullImage(): void {
  if (!usingThumb.value) return;
  const itemId = props.item.id;
  usingThumb.value = false; // don't kick off twice
  fetchItemObjectUrl(itemId)
    .then((url) => {
      if (props.item.id === itemId) mediaUrl.value = url;
    })
    .catch(() => {
      // Keep showing the thumb — zoom is just soft instead of broken.
    });
}

// --- Copy link: LINK items no longer offer an open-in-browser affordance
// (a LIFF webview handles external URLs poorly); the URL is copied to the
// clipboard instead. `copied` drives a brief "คัดลอกแล้ว" confirmation.
const copied = ref(false);
let copiedTimer: ReturnType<typeof setTimeout> | null = null;

async function copyLink() {
  const url = props.item.linkUrl;
  if (!url) return;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
    } else {
      // Fallback for webviews without the async Clipboard API.
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    copied.value = true;
    if (copiedTimer) clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => (copied.value = false), 1600);
  } catch {
    // Clipboard blocked — leave the button label unchanged rather than lie.
  }
}

onUnmounted(() => {
  if (copiedTimer) clearTimeout(copiedTimer);
});

// --- Zoom (images only): tap toggles a fixed 2x zoom centered on the tap point;
// dragging while zoomed pans instead of swiping to the next item.
// `zoomed`/`panX`/`panY` are declared above (before the media-loading watch) to
// avoid a temporal-dead-zone crash — see the comment there. ---
const stage = ref<HTMLElement | null>(null);

function toggleZoom(e: PointerEvent) {
  if (!isImage.value || !stage.value) return;
  if (zoomed.value) {
    zoomed.value = false;
    panX.value = 0;
    panY.value = 0;
    return;
  }
  const rect = stage.value.getBoundingClientRect();
  const offsetX = e.clientX - (rect.left + rect.width / 2);
  const offsetY = e.clientY - (rect.top + rect.height / 2);
  panX.value = -offsetX;
  panY.value = -offsetY;
  zoomed.value = true;
  loadFullImage(); // zooming is the signal the ~512px thumb is no longer enough
}

// --- Swipe navigation (non-zoomed only): horizontal drag follows the finger,
// commits to prev/next past a distance threshold, otherwise snaps back. ---
const SWIPE_COMMIT_PX = 80;
const dragging = ref(false); // actively swipe-dragging (not zoomed)
const panning = ref(false); // actively panning a zoomed image
const dragX = ref(0);
const dragStartX = ref(0);
const dragStartY = ref(0);
const pointerId = ref<number | null>(null);
const isTap = ref(true);

function onPointerDown(e: PointerEvent) {
  pointerId.value = e.pointerId;
  dragStartX.value = e.clientX;
  dragStartY.value = e.clientY;
  isTap.value = true;
  if (zoomed.value) {
    panning.value = true;
    return;
  }
  dragging.value = true;
  dragX.value = 0;
}

function onPointerMove(e: PointerEvent) {
  if (pointerId.value !== e.pointerId) return;
  const dx = e.clientX - dragStartX.value;
  const dy = e.clientY - dragStartY.value;

  if (zoomed.value) {
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) isTap.value = false;
    if (isTap.value) return;
    // Clamp so a long drag can't push the zoomed image fully out of frame.
    const rect = stage.value?.getBoundingClientRect();
    const maxX = rect ? rect.width * 0.5 : 200;
    const maxY = rect ? rect.height * 0.5 : 200;
    panX.value = Math.min(maxX, Math.max(-maxX, panX.value + e.movementX));
    panY.value = Math.min(maxY, Math.max(-maxY, panY.value + e.movementY));
    return;
  }

  if (Math.abs(dx) > 4 || Math.abs(dy) > 4) isTap.value = false;
  if (!dragging.value) return;
  // A predominantly vertical drag isn't a swipe attempt; ignore it so it doesn't
  // fight a future vertical gesture (e.g. a scrollable media type added later).
  if (Math.abs(dy) > Math.abs(dx) * 1.5) return;
  if ((dx > 0 && !props.hasPrev) || (dx < 0 && !props.hasNext)) {
    dragX.value = dx / 3; // soft resistance at the edges
  } else {
    dragX.value = dx;
  }
}

function onPointerUp(e: PointerEvent) {
  if (pointerId.value !== e.pointerId) return;
  pointerId.value = null;

  if (zoomed.value) {
    panning.value = false;
    if (isTap.value) toggleZoom(e);
    return;
  }

  if (isTap.value) {
    dragging.value = false;
    dragX.value = 0;
    toggleZoom(e);
    return;
  }

  dragging.value = false;
  if (dragX.value >= SWIPE_COMMIT_PX && props.hasPrev) emit("prev");
  else if (dragX.value <= -SWIPE_COMMIT_PX && props.hasNext) emit("next");
  dragX.value = 0;
}

const stageTransform = computed(() => {
  if (zoomed.value) return `translate(${panX.value}px, ${panY.value}px) scale(2)`;
  return `translateX(${dragX.value}px)`;
});
// No transition while actively swiping or panning (1:1 finger tracking); animate
// everything else — zoom toggle in/out, and the post-release swipe snap/commit.
// Reduced motion skips the transform transition entirely; state still changes,
// just without the animated snap.
const stageTransition = computed(() =>
  prefersReducedMotion || dragging.value || panning.value ? "none" : "transform 250ms cubic-bezier(0.25,1,0.5,1)"
);
</script>

<template>
  <Teleport to="body">
    <Transition :name="prefersReducedMotion ? 'detail-fade' : 'detail-rise'" appear>
      <div
        ref="root"
        class="fixed inset-0 z-[90] flex flex-col bg-surface"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
      >
        <div class="flex h-14 shrink-0 items-center justify-between px-container-margin">
          <button
            ref="closeButton"
            type="button"
            aria-label="ปิด"
            class="flex h-11 w-11 items-center justify-center rounded-full text-on-surface-variant transition-all active:scale-95 hover:bg-surface-container-high"
            @click="emit('close')"
          >
            <span class="material-symbols-outlined text-[22px]">close</span>
          </button>
          <span v-if="position" class="font-metadata text-on-surface-variant">{{ position }}</span>
          <span class="w-11" aria-hidden="true" />
        </div>

        <div
          ref="stage"
          class="relative min-h-0 flex-1 touch-none select-none overflow-hidden bg-surface-container-highest"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
        >
          <div
            class="flex h-full w-full items-center justify-center"
            :style="{ transform: stageTransform, transition: stageTransition }"
          >
            <img
              v-if="isImage && mediaUrl"
              :src="mediaUrl"
              :alt="title"
              class="max-h-full max-w-full object-contain"
              draggable="false"
              @error="onMediaElementError"
            />
            <video
              v-else-if="isVideo && mediaUrl"
              :src="mediaUrl"
              controls
              playsinline
              class="max-h-full max-w-full"
              @error="onMediaElementError"
            />
            <div v-else-if="mediaLoading" class="h-2/3 w-2/3 animate-pulse rounded-2xl bg-surface-container-high" aria-hidden="true" />
            <div v-else-if="mediaFailed" class="flex flex-col items-center gap-1 px-8 text-center">
              <p class="font-body-sm text-on-surface-variant">แสดงตัวอย่างไม่สำเร็จ</p>
              <p v-if="mediaErrorMsg" class="font-metadata text-on-surface-variant/70 break-all">{{ mediaErrorMsg }}</p>
            </div>
            <div
              v-else-if="item.kind === 'LINK'"
              class="flex w-full max-w-sm flex-col gap-3 px-6 text-center"
            >
              <img
                v-if="item.ogImageUrl"
                :src="item.ogImageUrl"
                alt=""
                class="aspect-[1.91/1] w-full rounded-xl object-cover"
              />
              <span class="flex h-16 w-16 shrink-0 self-center items-center justify-center rounded-2xl" :class="kindMeta.tile">
                <span class="material-symbols-outlined text-[28px]">{{ kindMeta.icon }}</span>
              </span>
              <p v-if="item.ogDescription" class="font-body-sm text-on-surface-variant">{{ item.ogDescription }}</p>
            </div>
            <!-- Documents (PDF / Excel / Word / …): no inline render — a type
                 icon stands in for the file, with its name below. -->
            <div v-else class="flex flex-col items-center gap-3 px-8 text-center">
              <span class="flex h-24 w-24 items-center justify-center rounded-2xl" :class="kindMeta.tile">
                <span class="material-symbols-outlined text-[44px]">{{ kindMeta.icon }}</span>
              </span>
              <p class="font-body-sm text-on-surface-variant break-all" style="text-wrap: pretty">{{ title }}</p>
            </div>
          </div>
        </div>

        <div class="flex shrink-0 flex-col gap-stack-md overflow-y-auto px-container-margin py-stack-md safe-pb">
          <div class="flex flex-col gap-1">
            <div class="flex items-start gap-2">
              <span class="min-w-0 flex-1 font-body-md font-medium text-on-surface" style="text-wrap: pretty">{{
                title
              }}</span>
              <span
                v-if="item.isDuplicate"
                class="shrink-0 rounded-full bg-secondary-container px-2 py-0.5 text-[10px] font-bold text-on-secondary-container"
                >ซ้ำ</span
              >
            </div>
            <button
              v-if="item.linkUrl"
              type="button"
              class="flex items-center gap-1 self-start font-body-sm font-medium text-primary transition-colors active:scale-[0.98]"
              @click="copyLink"
            >
              <span class="material-symbols-outlined text-[16px]">{{ copied ? "check" : "content_copy" }}</span>
              {{ copied ? "คัดลอกแล้ว" : "คัดลอกลิงก์" }}
            </button>
            <p class="font-metadata text-on-surface-variant">
              {{ senderLabel(item) }} • {{ dateTimeLabel }}
            </p>
            <div class="flex items-center gap-2 font-metadata text-on-surface-variant">
              <span v-if="sizeLabel">{{ sizeLabel }}</span>
              <span v-if="sizeLabel">•</span>
              <span class="font-bold tracking-tight" :class="statusReady ? 'text-status-ready' : 'text-error'">{{
                statusReady ? "READY" : "BLOCKED"
              }}</span>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <button
              v-if="!trash && item.kind !== 'LINK'"
              type="button"
              class="flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-surface-container-high font-label-caps text-on-surface-variant transition-all active:scale-[0.98]"
              @click="emit('download', item)"
            >
              <span class="material-symbols-outlined text-[18px]">download</span>
              ดาวน์โหลด
            </button>
            <button
              v-if="!trash"
              type="button"
              class="flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-error-container font-label-caps text-on-error-container transition-all active:scale-[0.98]"
              @click="emit('delete', item.id)"
            >
              <span class="material-symbols-outlined text-[18px]">delete</span>
              ลบ
            </button>
            <button
              v-else
              type="button"
              class="flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-primary-container font-label-caps text-on-primary-container transition-all active:scale-[0.98]"
              @click="emit('restore', item.id)"
            >
              <span class="material-symbols-outlined text-[18px]">restore</span>
              กู้คืน
            </button>
          </div>

          <!-- Report: intentionally understated relative to the row above — a
               bare text trigger, not a fourth pill button, since reporting is
               not a primary action (DESIGN.md §5 components: buttons carry
               weight, this shouldn't). Opens the dedicated /report screen. -->
          <button
            type="button"
            class="flex items-center gap-1 self-start font-metadata text-on-surface-variant transition-colors active:scale-[0.98] hover:text-on-surface"
            @click="emit('report')"
          >
            <span class="material-symbols-outlined text-[14px]">flag</span>
            รายงานเนื้อหานี้
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.detail-rise-enter-active,
.detail-rise-leave-active {
  transition:
    opacity 250ms cubic-bezier(0.25, 1, 0.5, 1),
    transform 250ms cubic-bezier(0.25, 1, 0.5, 1);
}
.detail-rise-leave-active {
  transition-duration: 190ms;
}
.detail-rise-enter-from,
.detail-rise-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.detail-fade-enter-active,
.detail-fade-leave-active {
  transition: opacity 120ms linear;
}
.detail-fade-enter-from,
.detail-fade-leave-to {
  opacity: 0;
}
</style>
